import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import api, { formatApiError } from "../lib/axiosClient";
import { BrandMark, BRAND_PROGRAM } from "../components/Brand";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) { setError("Please enter your email."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err, "Couldn’t send the reset link."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="inline-flex items-center gap-3 mb-8">
          <BrandMark className="h-10 w-auto" />
          <span className="h-7 w-px bg-[#E8E2D9]" aria-hidden="true" />
          <Link
            to="/"
            className="font-serif-display text-base text-[#2D2A26] font-semibold hover:text-[#C86B5E] transition-colors"
          >
            {BRAND_PROGRAM}
          </Link>
        </div>

        <div className="bg-white border border-[#E8E2D9] rounded-3xl p-8 sm:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <div className="w-12 h-12 mx-auto rounded-full bg-[#EBF2EA] flex items-center justify-center mb-4">
                <MailCheck className="w-5 h-5 text-[#8A9A86]" strokeWidth={2} />
              </div>
              <h1 className="font-serif-display text-2xl text-[#2D2A26] font-semibold">Check your inbox</h1>
              <p className="text-sm text-[#5C5A56] mt-2">
                If an account exists for <span className="font-semibold">{email}</span>, we’ve sent a reset link.
                It’s valid for one hour.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#C86B5E] hover:underline"
              >
                Back to sign in <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-serif-display text-3xl text-[#2D2A26] font-semibold">Reset your password</h1>
              <p className="mt-2 text-sm text-[#5C5A56]">
                Enter your email and we’ll send a single-use link to set a new password.
              </p>
              <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8A8782] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    data-testid="forgot-email-input"
                    className="w-full px-5 py-3 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] focus:outline-none focus:border-[#C86B5E] transition-colors"
                  />
                </div>
                {error && (
                  <div className="rounded-2xl bg-[#FDF5EE] border border-[#F8EBE9] p-3 text-sm text-[#C86B5E]" data-testid="forgot-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="forgot-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#C86B5E] text-white rounded-full px-7 py-3.5 font-medium hover:bg-[#B35B4E] disabled:opacity-60 transition-all"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
              <div className="mt-6 text-center text-sm text-[#5C5A56]">
                Remembered it?{" "}
                <Link to="/login" className="text-[#C86B5E] font-semibold hover:underline">
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
